import React from 'react';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import {
  Card,
  Grid,
  Button,
  SvgIcon,
  FormLabel,
  Typography,
  CardHeader,
  CardContent,
  CardActions,
  FormHelperText,
  Box,
} from '@mui/material';

import {
  Loading,
  Asterisk,
  TextField,
  SnakebarToaster,
  UploadFileInput,
  SimpleRadioGroupField,
  AutoCompleteSingleSelect,
  AlertTooltip,
} from '@/components';
import { trancatFileName } from '@/features/shared/utils';
import { stylesButtonIconSquareSM } from '@/utils/globalStyles';
import { AlertOutlineIcon, ArrowTop, SendPlaneLineIcon } from '@/utils/iconSVG';

import {
  stylesAutoCompleteSingleSelect,
  stylesSubscribeAdditionnalInformationCard,
  stylesSubscribeAdditionnalInformationCardContent,
  stylesSubscribeAdditionnalInformationFormSubtitle,
  stylesSubscribeAdditionnalInformationCardSubheader,
} from './SubscribeAdditionnalInformationForm.styles';
import {
  useFileTooltipError,
  useCompanyTypesOptions,
  usePaymentMethodOptions,
  useSubscribeAdditionnalInformationForm,
} from './hooks';
import { REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM } from './types';

export const SubscribeAdditionnalInformationForm: React.FunctionComponent = () => {
  const { t } = useTranslation('borderCrossingRORO_onBoarding');
  const {
    isSubmitSubscribeAdditionnalInformationFormInError,
    isSubmitSubscribeAdditionnalInformationPending,
    formState: { errors },
    control,
    register,
    handleCloseToaster,
    handleCancelTheRequest,
    handleSubmitSubscribeAdditionnalInformation,
  } = useSubscribeAdditionnalInformationForm();

  const { companyTypeOptions, isGetCompanyTypesLoading } = useCompanyTypesOptions();
  const { paymentMethodOptions, isGetPaymentMethodLoading } = usePaymentMethodOptions();
  const { fileTooltipError, getFileTooltipError, checkIsFileTypeValid } = useFileTooltipError();

  if (isGetCompanyTypesLoading || isGetPaymentMethodLoading) {
    return <Loading />;
  }

  return (
    <Card sx={stylesSubscribeAdditionnalInformationCard}>
      <CardHeader
        title={
          <Typography variant='h1' color='primary.dark2'>
            {t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.label')}
          </Typography>
        }
        subheader={
          <Typography variant='h2' color='primary.dark2' sx={stylesSubscribeAdditionnalInformationCardSubheader}>
            {t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.customsProENS.label')}
          </Typography>
        }
        disableTypography
      />
      <CardContent sx={stylesSubscribeAdditionnalInformationCardContent}>
        <Typography component='div' variant='bodyMDMedium' sx={stylesSubscribeAdditionnalInformationFormSubtitle}>
          {t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.companyInformation.label')}
        </Typography>
        <Grid container spacing={2} item xs={6} pb={3}>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <TextField
              isRequired
              sx={{ mb: 0 }}
              labelSx={{ pb: 1 }}
              error={errors.registrationNumber}
              id={REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.registrationNumber}
              registration={register(REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.registrationNumber)}
              label={t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.registrationNumber.label')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <TextField
              isRequired
              sx={{ mb: 0 }}
              labelSx={{ pb: 1 }}
              variant='outlined'
              error={errors.vatNumber}
              id={REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.vatNumber}
              registration={register(REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.vatNumber)}
              label={t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.vatNumber.label')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <TextField
              sx={{ mb: 0 }}
              variant='outlined'
              labelSx={{ pb: 1 }}
              error={errors.eori}
              id={REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.eori}
              registration={register(REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.eori)}
              label={t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.eoriNumber.label')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <Controller
              control={control}
              name={REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.companyTypeId}
              render={({ field: { value: prevValue, onChange } }) => (
                <AutoCompleteSingleSelect
                  multiple={false}
                  labelSx={{ pb: 1 }}
                  options={companyTypeOptions}
                  error={errors.companyTypeId}
                  sx={stylesAutoCompleteSingleSelect}
                  getOptionLabel={(option) => option.label}
                  value={companyTypeOptions.find((item) => item.value === prevValue) ?? null}
                  label={t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.companyType.label')}
                  onChange={(_, { value }) => {
                    onChange?.(value);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <TextField
              sx={{ mb: 0 }}
              variant='outlined'
              labelSx={{ pb: 1 }}
              error={errors.weeklyDispatchVolume}
              id={REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.weeklyDispatchVolume}
              registration={register(REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.weeklyDispatchVolume)}
              label={t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.weeklyDispatchVolume.label')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <FormLabel id={REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.kbisFile}>
              <Trans
                i18nKey={t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.pdfKbis.label')}
                components={{
                  box: <Box display='flex' />,
                  asterisk: <Asterisk />,
                  text1: <Box component='span' />,
                  text2: (
                    <Typography sx={{ fontSize: 'bodyXS.fontSize', color: 'grey.500', fontStyle: 'italic', pb: 2 }} />
                  ),
                }}
              />
            </FormLabel>
            <Controller
              control={control}
              name={REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.kbisFile}
              render={({ fieldState: { invalid, error }, field: { value, onChange } }) => (
                <>
                  <Box component='span' display='flex'>
                    <UploadFileInput
                      variant='contained'
                      color='primaryLight'
                      sx={{ ...stylesButtonIconSquareSM, pb: 0, pt: 0 }}
                      onFileUpload={(event) => {
                        const newFile = event.target.files[0];
                        const isFileTypeValid = checkIsFileTypeValid(newFile);
                        if (isFileTypeValid) {
                          onChange(newFile);
                        } else {
                          getFileTooltipError(newFile);
                        }
                      }}
                      accept='.pdf, image/png, image/jpeg'
                      startIcon={<SvgIcon component={ArrowTop} sx={{ fontSize: 'bodySM.fontSize' }} />}
                    >
                      {t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.file.label')}
                      {fileTooltipError.isError && (
                        <AlertTooltip
                          color={fileTooltipError.color}
                          message={fileTooltipError.message}
                          backgroundColor={fileTooltipError.backgroundColor}
                          icon={{ svg: AlertOutlineIcon, color: 'error.main' }}
                        />
                      )}
                    </UploadFileInput>
                    <Box component='span' sx={stylesCustomDocListItemFile}>
                      {value?.name
                        ? trancatFileName(value.name)
                        : t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.noFileImported.label')}
                    </Box>
                  </Box>
                  {invalid && <FormHelperText sx={{ color: '#F1416C' }}>{error?.message}</FormHelperText>}
                </>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <TextField
              sx={{ mb: 0 }}
              variant='outlined'
              labelSx={{ pb: 1 }}
              error={errors.invoicingEmail}
              id={REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.invoicingEmail}
              registration={register(REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.invoicingEmail)}
              label={t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form..invoicingEmaillabel')}
            />
          </Grid>
        </Grid>
        <Typography component='div' variant='bodyMDMedium' sx={stylesSubscribeAdditionnalInformationFormSubtitle}>
          {t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.paymentMethod.label')}
          <Asterisk />
        </Typography>
        <Grid container spacing={2} item xs={6}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <SimpleRadioGroupField
              control={control}
              labelSx={{ pb: 1 }}
              error={errors.paymentMethodId}
              options={paymentMethodOptions}
              sx={{ m: 0, mb: 2, p: 0, width: '100%' }}
              name={REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.paymentMethodId}
            />
          </Grid>
        </Grid>
        <Typography component='div' variant='bodyMDMedium' sx={stylesSubscribeAdditionnalInformationFormSubtitle}>
          {t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.bankInformation.label')}
        </Typography>
        <Grid container spacing={2} item xs={6}>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <TextField
              fullWidth
              isRequired
              sx={{ mb: 0 }}
              variant='outlined'
              labelSx={{ pb: 1 }}
              error={errors.bankName}
              id={REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.bankName}
              registration={register(REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.bankName)}
              label={t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.bankName.label')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <TextField
              fullWidth
              isRequired
              sx={{ mb: 0 }}
              variant='outlined'
              labelSx={{ pb: 1 }}
              error={errors.holderName}
              id={REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.holderName}
              registration={register(REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.holderName)}
              label={t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.holderName.label')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={9} lg={9}>
            <TextField
              fullWidth
              isRequired
              sx={{ mb: 0 }}
              variant='outlined'
              labelSx={{ pb: 1 }}
              error={errors.iban}
              id={REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.iban}
              registration={register(REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.iban)}
              label={t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.iban.label')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={3}>
            <TextField
              fullWidth
              isRequired
              sx={{ mb: 0 }}
              variant='outlined'
              labelSx={{ pb: 1 }}
              error={errors.bic}
              id={REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.bic}
              registration={register(REGISTER_SUBSCRIBE_ADDITIONNAL_INFORMATIONFORM_FORM.bic)}
              label={t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.bic.label')}
            />
          </Grid>
        </Grid>
        {isSubmitSubscribeAdditionnalInformationFormInError && (
          <SnakebarToaster
            isOpen
            severity='error'
            content={t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.error.toasterMessage', {
              supportEmail: process.env.REACT_APP_SUPPORT_EMAIL,
            })}
            onClose={handleCloseToaster}
            autoHideDuration={null}
          />
        )}
      </CardContent>
      <CardActions sx={{ pb: 3, width: '50%' }}>
        <Button variant='contained' color='inherit' sx={{ mr: 'auto' }} onClick={handleCancelTheRequest}>
          {t('borderCrossingRORO.onBoarding.subscribeAdditionnalInformation.form.cancelButton.label')}
        </Button>
        <Button
          startIcon={<SendPlaneLineIcon />}
          color='success'
          variant='contained'
          disabled={isSubmitSubscribeAdditionnalInformationPending}
          onClick={handleSubmitSubscribeAdditionnalInformation}
        >
          {t('borderCrossingRORO.onBoarding.subscribe.form.submitSubscribeAdditionnalInformation.label')}
        </Button>
      </CardActions>
    </Card>
  );
};
export const stylesCustomDocListItemFile = {
  fontSize: 'bodySM.fontSize',
  alignItems: 'center',
  color: 'grey.500',
  fontWeight: 500,
  display: 'flex',
  textOverflow: 'ellipsis',
  verticalAlign: 'middle',
  overflow: 'hidden',
  textWrap: 'nowrap',
  pl: 1,
};
